import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import tradeService from '../services/tradeService';
import Modal from './Modal';
import SwapIcon from './SwapIcon';
import './TradeIcon.css'; // Reusing the icon styles
import './TradeInsertSetIcon.css'; // Modal specific styles

/**
 * A trade icon component for insert sets that opens a modal on click
 * 
 * @param {Object} props
 * @param {Object} props.insertSet - The insert set to add cards to trade list
 * @param {boolean} props.disabled - Whether the button should be disabled
 * @param {string} props.className - Additional classes to apply
 * @param {number} props.size - Icon size in pixels (default: 24)
 * @param {Function} props.onTradeUpdate - Callback function to refresh cards after trade update
 */
const TradeInsertSetIcon = ({ 
  insertSet,
  disabled = false, 
  className = '',
  size = 24,
  onTradeUpdate = null
}) => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quantityThreshold, setQuantityThreshold] = useState(1);

  // Handle click event
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If user is not logged in, redirect to login
    if (!currentUser) {
      navigate('/login', { 
        state: { 
          from: window.location.pathname,
          message: 'Please log in to add items to your trade list'
        } 
      });
      return;
    }

    if (!insertSet || !insertSet.id) {
      console.error('Invalid insert set provided to TradeInsertSetIcon:', insertSet);
      return;
    }

    // Show modal for selection
    setShowModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Handle adding all cards to trade list
  const handleAddAllCards = async () => {
    setIsSubmitting(true);
    
    try {
      await tradeService.addInsertSetForTrade(insertSet.id, 'all');
      showToast(`Added all cards from ${insertSet.name} to trade list`, 'success');
      setShowModal(false);
      if (onTradeUpdate) onTradeUpdate();
    } catch (err) {
      console.error('Error adding insert set cards to trade list:', err);
      showToast('Failed to update trade list', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle adding only cards in collection to trade list
  const handleAddCollectionCards = async () => {
    setIsSubmitting(true);
    
    try {
      await tradeService.addInsertSetForTrade(insertSet.id, 'collection');
      showToast(`Added cards from ${insertSet.name} in your collection to trade list`, 'success');
      setShowModal(false);
      if (onTradeUpdate) onTradeUpdate();
    } catch (err) {
      console.error('Error adding collection insert set cards to trade list:', err);
      showToast('Failed to update trade list', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle adding cards with quantity greater than threshold to trade list
  const handleAddQuantityCards = async () => {
    setIsSubmitting(true);
    
    try {
      await tradeService.addInsertSetForTrade(insertSet.id, 'quantity', quantityThreshold);
      showToast(`Added cards from ${insertSet.name} with quantity > ${quantityThreshold} to trade list`, 'success');
      setShowModal(false);
      if (onTradeUpdate) onTradeUpdate();
    } catch (err) {
      console.error('Error adding quantity insert set cards to trade list:', err);
      showToast('Failed to update trade list', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button 
        className={`trade-icon-btn ${className}`}
        onClick={handleClick}
        disabled={disabled}
        title="Add cards from this set to trade list"
        aria-label="Add cards from this set to trade list"
      >
        <SwapIcon size={size} />
      </button>
      
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Add to Trade List"
      >
        <div className="trade-insert-set-modal">
          <p>How would you like to add cards from this set to your trade list?</p>
          
          <div className="trade-insert-set-modal__buttons">
            <button 
              className="button button--primary"
              onClick={handleAddAllCards}
              disabled={isSubmitting}
            >
              Add all cards
            </button>
            <button 
              className="button button--secondary"
              onClick={handleAddCollectionCards}
              disabled={isSubmitting}
            >
              Add only cards in my collection
            </button>
            <div>
              <button 
                className="button button--secondary"
                onClick={handleAddQuantityCards}
                disabled={isSubmitting}
              >
                Add cards with quantity greater than
              </button>
              <div className="trade-insert-set-modal__quantity-option">
                <input 
                  type="number" 
                  className="trade-insert-set-modal__quantity-input"
                  value={quantityThreshold}
                  min="1"
                  onChange={(e) => setQuantityThreshold(Math.max(1, parseInt(e.target.value) || 1))}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <button 
              className="button button--text"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TradeInsertSetIcon;